import { useCallback, useState, useEffect, useRef } from "react";
import { AxiosError } from "axios";
import {
  validate,
  EditComponentErrorResponse,
  FormSchemaDto,
  EditComponentErrorGroup,
} from "@amzn/crowd-front-end-common-library";
import useAsync from "../../http/useAsync";
import { LoadingState } from "../../http";
import httpRequestWithMetrics from "../../http/httpRequestWithMetrics";
import { MetricsState } from "../../logging";
import { CreateTeamInputStepResult as InputStepResult } from "./CreateTeamInputStepResult";
import { TeamOwnerDto } from "../../common/model/team.dto";

interface UseCreateTeamValidateHookResult {
  execute: () => void;
  thrownValidationError?: Error;
  loadingState: LoadingState;
  validationErrors?: EditComponentErrorResponse;
  asyncValidateResult?: EditComponentErrorResponse;
}

async function validateInputAsync(
  parentTeamId: string,
  localChanges: Partial<InputStepResult>,
  metrics: MetricsState
): Promise<any> {
  const owner = localChanges.owner
    ? {
        employeeId: localChanges.owner.attributes
          ? localChanges.owner.attributes.employeeId
          : "",
      }
    : null;

  /**
   * Constructing admins array based on input provided in the CreateTeam form.
   * if (admins are provided in the form):
   *    if (owner is provided in the form):
   *        Add all the admins in the array as well as the owner
   *    else
   *        Add all the admins
   * else if (owner is provided in the form):
   *    Add only the owner
   * else
   *    return an empty array, meaning no admins
   */
  const admins = localChanges.admins
    ? owner
      ? localChanges.admins
          .map((admin) => ({
            employeeId: admin.attributes ? admin.attributes.employeeId : "",
          }))
          .filter((admin) => admin.employeeId !== owner.employeeId)
          .concat(owner as TeamOwnerDto)
      : localChanges.admins.map((admin) => ({
          employeeId: admin.attributes ? admin.attributes.employeeId : "",
        }))
    : owner
    ? [owner]
    : [];

  const team = {
    name: localChanges.name,
    owner,
    reportsTo: parentTeamId,
  };

  const response = await httpRequestWithMetrics(
    "createTeamValidateAsync",
    metrics,
    {
      url: `/api/v1/teams/validate`,
      method: "POST",
      data: {
        team,
        admins,
      },
    }
  );
  return response.data;
}

/**
 * Custom react hook that abstracts the validation logic for create team workflow.
 *
 * @param {FormSchemaDto} formSchema
 * @param {string} parentTeamId
 * @param {Partial<InputStepResult>} localChanges
 * @param {MetricsState} metrics
 * @param {(localChanges: Partial<InputStepResult>) => void} onPassValidation
 * @returns {UseCreateTeamValidateHookResult}
 */
function useCreateTeamValidate(
  formSchema: FormSchemaDto,
  parentTeamId: string,
  localChanges: Partial<InputStepResult>,
  metrics: MetricsState,
  onPassValidation: (localChanges: Partial<InputStepResult>) => void
): UseCreateTeamValidateHookResult {
  const changesToValidate = useRef<Partial<InputStepResult>>();
  const validatedChanges = useRef<Partial<InputStepResult>>();
  const [validationErrors, setValidationErrors] = useState<
    EditComponentErrorResponse
  >();

  const {
    loadingState,
    execute: executeAsyncValidate,
    error,
    response: asyncValidateResult,
  } = useAsync(validateInputAsync, [parentTeamId, localChanges, metrics]);

  useEffect(() => {
    const httpError = error as AxiosError;
    if (httpError && httpError.response && httpError.response.status === 400) {
      // 400 Errors are validation errors. Treat other errors differently.
      setValidationErrors(httpError.response.data);
    }
  }, [error]);

  useEffect(() => {
    if (asyncValidateResult && asyncValidateResult.errors.length > 0) {
      const errors = asyncValidateResult.errors.map(
        (asyncError: { attribute: string; errorMessage: string }) => {
          return {
            fieldName: asyncError.attribute,
            message: asyncError.errorMessage,
            validation: "asyncValidation",
          };
        }
      );

      const asyncError: EditComponentErrorResponse = {
        errors: [
          {
            groupId: "general-group",
            groupLabel: "Team",
            errors,
          },
        ],
      };
      setValidationErrors(asyncError);
    } else if (
      loadingState === LoadingState.RESOLVED &&
      changesToValidate.current &&
      asyncValidateResult.errors.length === 0
    ) {
      validatedChanges.current = changesToValidate.current;
      setValidationErrors(undefined);
      onPassValidation(changesToValidate.current);
    }
  }, [loadingState, onPassValidation, asyncValidateResult]);

  const execute = useCallback(() => {
    if (validatedChanges.current === localChanges) {
      // Instantly validate changes that have already been validated.
      onPassValidation(validatedChanges.current);
      return;
    }

    const modifiedTeam = { ...localChanges };
    const syncValidateResult = validate(modifiedTeam, formSchema);
    if (modifiedTeam.ownerInputValue && !modifiedTeam.owner) {
      const errors = [
        {
          fieldName: "owner",
          message:
            "Team owner is not valid. Select an employee from the search results.",
          validation: "ownerInputValueValidation",
        },
      ];
      const ownerInputValueError: EditComponentErrorGroup = {
        groupId: "general-group",
        groupLabel: "Team",
        errors,
      };
      syncValidateResult.errors.push(ownerInputValueError);
    }

    if (syncValidateResult.errors.length > 0) {
      setValidationErrors(syncValidateResult);
    } else {
      changesToValidate.current = localChanges;
      setValidationErrors(undefined);
      executeAsyncValidate();
    }
  }, [localChanges, executeAsyncValidate, onPassValidation, formSchema]);

  return {
    execute,
    thrownValidationError: error as Error | undefined,
    loadingState,
    validationErrors,
  };
}

export default useCreateTeamValidate;
